import React, { useState } from 'react';
import {
	ApplicationStatus,
	ApprovalType,
	PositionTitle,
	UIApplication,
	UIExtendedApplication,
	UIRemarksDrawerType,
	UserRole,
} from 'types';
import { generatePositionTitle } from 'shared-utils';
import { Button, Modal } from 'flowbite-react';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Core viewer
import { Viewer } from '@react-pdf-viewer/core';

// Plugins
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { useMutation, useQueryClient } from 'react-query';
import { useToast } from 'src/context/ToastProvider';
import {
	acceptChangedApplicationTerms,
	approveAction,
	offerLetterResponse,
	rejectAction,
	requestOptingOutOfApplicationProcess,
} from 'src/apis/application';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OptOutModal from '../application-card-v2/OptOutModal';
import { useUserContext } from 'src/context/UserProvider';
import RemarksDrawer from '../application-card-v2/RemarksDrawer';
import { Drawer } from 'src/components/shared/Drawer';
import RecruiterCandidateSendOfferLetter from 'src/components/recruiterPositions/RecruiterCandidateSendOfferLetter';
import OfferedPositionParameters from './OfferedPositionParameters';

// Required for rendering PDFs in a browser
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface Props {
	application: UIApplication | UIExtendedApplication;
	companyName?: string;
	positionTitle?: string;
	positionId?: string;
}

const ViewUpdatedTerms = ({ positionId, application, companyName: company, positionTitle: position }: Props) => {
	// Create new plugin instance
	const { user } = useUserContext();
	const { isJobseekerLoggedIn } = useUserContext();
	const defaultLayoutPluginInstance = defaultLayoutPlugin();
	const companyName = application?.position?.company?.name || company || '';
	const toast = useToast();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const onComplete = () => {
		setIsLoading(false);
	};
	const [showDeclineModal, setShowDeclineModal] = useState(false);
	const [showAcceptConfirmModal, setShowAcceptConfirmModal] = useState(false);
	const [showDeclineConfirmModal, setShowDeclineConfirmModal] = useState(false);
	const [declineReason, setDeclineReason] = useState<string>('');
	const requestOptingOutOfApplicationProcessMutation = useMutation(
		['optOutOfApplicationProcess'],
		requestOptingOutOfApplicationProcess,
		{
			onSuccess() {
				toast({
					title: 'You have successfully requested to opt out of the application process',
					type: 'success',
				});
				onComplete();
				queryClient.invalidateQueries(['getAllApplications']);
				queryClient.refetchQueries(['getAllApplications']);

				const newSearchParams = new URLSearchParams(searchParams);
				newSearchParams.delete('viewUpdatedTerms');
				setSearchParams(newSearchParams);
				navigate(`/my-applications?${newSearchParams.toString()}`);
			},
			onError(error: Error) {
				onComplete();
				toast({
					title: `Error while requesting opt out of the application process: ${error.message}`,
					type: 'error',
				});
			},
		},
	);

	const acceptChangedApplicationTermsMutation = useMutation(
		['acceptChangedApplicationTerms'],
		acceptChangedApplicationTerms,
		{
			onSuccess() {
				toast({
					title: 'Action Successful!',
					type: 'success',
				});
				onComplete();
				queryClient.invalidateQueries(['getAllApplications']);
				queryClient.refetchQueries(['getAllApplications']);

				const newSearchParams = new URLSearchParams(searchParams);
				newSearchParams.delete('viewUpdatedTerms');
				setSearchParams(newSearchParams);
				navigate(`/my-applications?${newSearchParams.toString()}`);
			},
			onError(error: Error) {
				onComplete();
				toast({
					title: `Error while accepting changed application terms: ${error.message}`,
					type: 'error',
				});
			},
		},
	);
	return (
		<>
			<div className="mt-[18px] pb-[100px] ">
				<OfferedPositionParameters
					companyName={application.position?.company?.name ?? ''}
					positionName={generatePositionTitle(application.position?.title ?? '')}
					offeredCtc={application.offeredPositionParameters?.ctc + ''}
					offeredDesignation={generatePositionTitle(application.offeredPositionParameters?.positionTitle ?? '')}
					accManagerRemarks={application.offeredPositionParameters?.remarks ?? ''}
					internshipCtc={application?.offeredPositionParameters?.internshipCtc ?? 0}
					internshipDuration={application?.offeredPositionParameters?.internshipDuration ?? 0}
					dateOfJoining={application?.offeredPositionParameters?.dateOfJoining ?? undefined}
					jobType={application?.offeredPositionParameters?.offerType}
				/>
				{isJobseekerLoggedIn &&
					application.status === ApplicationStatus.CHANGED_OFFER_TERMS &&
					!application.history?.some(history => history.newStatus === ApplicationStatus.OFFER_ROLLED_OUT) ? (
					<div className="mt-[32px]">
						<div className="flex gap-[18px] items-center justify-between w-[100%]">
							<Button
								disabled={isLoading || acceptChangedApplicationTermsMutation.isLoading}
								onClick={() => {
									setShowAcceptConfirmModal(true);
								}}
								color="purplePlaced"
								className="w-[100%]"
							>
								Accept Offer
							</Button>
							<Button
								disabled={isLoading || acceptChangedApplicationTermsMutation.isLoading}
								onClick={() => {
									setShowDeclineConfirmModal(true);
								}}
								className="!bg-[#4B5563] hover:!bg-[#566171] !border !border-[#6B7280]] text-[#fff] w-[100%]"
							>
								Decline Offer
							</Button>
						</div>
					</div>
				) : null}
			</div>
			<Modal show={showAcceptConfirmModal} onClose={() => setShowAcceptConfirmModal(false)} dismissible size="md">
				<Modal.Body className="rounded-[8px] border border-solid dark:border-[#4B5563] bg-white dark:bg-[#1F2A37]">
					<div className="text-center text-[16px] font-[600] text-[#111928] dark:text-[#F2F2F5]">
						Are you sure you want to accept this offer?
					</div>
					<div className="mt-[20px] flex justify-center gap-4">
						<Button
							color="light"
							onClick={() => setShowAcceptConfirmModal(false)}
							disabled={acceptChangedApplicationTermsMutation.isLoading || isLoading}
						>
							No, cancel
						</Button>
						<Button
							color="purplePlaced"
							disabled={acceptChangedApplicationTermsMutation.isLoading || isLoading}
							isProcessing={acceptChangedApplicationTermsMutation.isLoading}
							onClick={() => {
								setShowAcceptConfirmModal(false);
								acceptChangedApplicationTermsMutation.mutate({
									applicationId: application._id || '',
									positionId: application.position?._id || '',
								});
							}}
						>
							Yes, accept
						</Button>
					</div>
				</Modal.Body>
			</Modal>
			<Modal show={showDeclineConfirmModal} onClose={() => setShowDeclineConfirmModal(false)} dismissible size="md">
				<Modal.Body className="rounded-[8px] border border-solid dark:border-[#4B5563] bg-white dark:bg-[#1F2A37]">
					<div className="text-center text-[16px] font-[600] text-[#111928] dark:text-[#F2F2F5]">
						Are you sure you want to decline this offer?
					</div>
					<div className="mt-[20px] flex justify-center gap-4">
						<Button
							color="light"
							onClick={() => setShowDeclineConfirmModal(false)}
							disabled={acceptChangedApplicationTermsMutation.isLoading || isLoading}
						>
							No, cancel
						</Button>
						<Button
							color="purplePlaced"
							disabled={acceptChangedApplicationTermsMutation.isLoading || isLoading}
							onClick={() => {
								setShowDeclineConfirmModal(false);
								setShowDeclineModal(true);
							}}
						>
							Yes, decline
						</Button>
					</div>
				</Modal.Body>
			</Modal>
			<OptOutModal
				isOpen={showDeclineModal}
				onClose={() => {
					setShowDeclineModal(false);
				}}
				optOutReason={declineReason}
				setOptOutReason={setDeclineReason}
				application={application}
				requestOptingOutOfApplicationProcessMutation={requestOptingOutOfApplicationProcessMutation}
			/>
		</>
	);
};

export default ViewUpdatedTerms;
